import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';

import * as qs from 'util/history-qs';
import { SearchTextbox } from 'view/search-textbox';
import { LoadingContainer } from 'view/common/loading-container';
import { HelpCenterCustomizeSidebar } from 'view/help-center-customize-sidebar';
import { DocumentTitle } from 'view/common/document-title';
import type { PushingCustomizeSidebarProps } from 'view/common/pushing-customize-sidebar/types';
import { sizes } from 'view/styles/sizes-viewport';
import type { InjectedCoordinationClientProps } from 'util/coordination-client-provider';
import { withCoordinationClient } from 'util/coordination-client-provider';
import { EngagementMessages } from 'view/common/constants';
import type { WithScreenSizeProps } from './use-screen-size';
import { withMatchScreenSize } from './use-screen-size';
import { LazyHelpCenterBucketsRoutes } from '../help-center-customize-sidebar/bucket-routes';
import messages from './messages';
import { HelpCenterContent } from './help-center-content';
import { SearchContainer, HelpCenterTitle, HelpCenterBucketsContainer } from './styled';
import { LazySuspense } from 'react-loosely-lazy';
import { isSearchEnhancementsMilestone1Enabled } from 'feature-flags';
import { Search } from 'view/search';

export interface HelpCenterProps extends InjectedIntlProps, WithScreenSizeProps {
    helpCenterTitle?: string;
    titleColor?: string;
    showCustomizeSidebar: boolean;
    sharedPortalName: string | undefined;
    onStartEditing: () => void;
    onFinishEditing: () => void;
    fetchHelpCenter: () => void;
    isLoading: boolean;
    isCustomizing: boolean;
    searchBarShown: boolean;
    canAdministerJira: boolean;
    isPortalHidingEnabled: boolean;
    PushingCustomizeSidebar: React.ComponentType<PushingCustomizeSidebarProps>;
}

interface ModifiedHTMLElement extends HTMLElement {
    ref: HTMLElement | null;
}

export interface State {
    showPulse: boolean;
}

export class HelpCenter extends React.Component<HelpCenterProps & InjectedCoordinationClientProps, State> {
    ref: HTMLElement | null;

    state = {
        showPulse: false,
    };

    handleChangeboarding = async () => {
        const client = this.props.coordinationClient;

        if (client) {
            const showPulse = await client.start(EngagementMessages.TOPICS_FOR_ALL);
            if (showPulse) {
                this.setState({ showPulse });
            }
        }
    };

    componentDidMount() {
        const { canAdministerJira, fetchHelpCenter, isMediaQueryMatch: isSmallScreenSize } = this.props;
        fetchHelpCenter();

        if (canAdministerJira && !isSmallScreenSize) {
            // Disabling existing violations, should be fixed when revisited.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.handleChangeboarding();
        }
    }

    componentDidUpdate(prevProps: HelpCenterProps & InjectedCoordinationClientProps) {
        const { coordinationClient: prevCoordinationClient } = prevProps;
        const { canAdministerJira, coordinationClient, isMediaQueryMatch: isSmallScreenSize } = this.props;

        if (prevCoordinationClient === undefined && Boolean(coordinationClient)) {
            if (canAdministerJira && !isSmallScreenSize) {
                // Disabling existing violations, should be fixed when revisited.
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.handleChangeboarding();
            }
        }
    }

    handleFinishEditing = () => {
        qs.set({ customize: undefined });
        this.props.onFinishEditing();
        this.setFocus();
    };

    setFocus() {
        this.ref && this.ref.focus();
    }

    onRef = (el: ModifiedHTMLElement | null) => {
        // https://github.com/styled-components/styled-components/issues/618
        this.ref = el && el.ref;
    };

    onOpen = () => {
        this.setState({ showPulse: false });
        this.props.onStartEditing();
    };

    render() {
        const {
            helpCenterTitle,
            titleColor,
            showCustomizeSidebar,
            sharedPortalName,
            intl,
            isCustomizing,
            isLoading,
            PushingCustomizeSidebar,
            searchBarShown,
            isMediaQueryMatch: isSmallScreenSize,
        } = this.props;
        const { showPulse } = this.state;

        const title = intl.formatMessage(messages.helpCenterTitle, { helpCenterTitle: sharedPortalName });

        return (
            <DocumentTitle title={title} ignoreBaseTitle>
                <LoadingContainer isLoading={isLoading}>
                    {showCustomizeSidebar && (
                        <LazySuspense fallback={null}>
                            {!searchBarShown ? (
                                <PushingCustomizeSidebar
                                    isOpen={isCustomizing}
                                    onOpen={this.onOpen}
                                    showPulse={showPulse && !isSmallScreenSize}
                                    onClose={this.handleFinishEditing}
                                >
                                    {!isSmallScreenSize ? (
                                        <HelpCenterBucketsContainer>
                                            <LazyHelpCenterBucketsRoutes
                                                isOpen={isCustomizing}
                                                onClose={this.handleFinishEditing}
                                            />
                                        </HelpCenterBucketsContainer>
                                    ) : (
                                        <HelpCenterCustomizeSidebar
                                            isOpen={isCustomizing}
                                            onClose={this.handleFinishEditing}
                                        />
                                    )}
                                </PushingCustomizeSidebar>
                            ) : null}
                        </LazySuspense>
                    )}

                    <SearchContainer>
                        <HelpCenterTitle titleColor={titleColor}>{helpCenterTitle}</HelpCenterTitle>
                        {/*Disabling existing violations, should be fixed when revisited.*/}
                        {/*eslint-disable-next-line jsx-a11y/no-autofocus*/}
                        {isSearchEnhancementsMilestone1Enabled() ? <Search /> : <SearchTextbox autoFocus={false} />}
                    </SearchContainer>

                    <HelpCenterContent isPortalHidingEnabled={this.props.isPortalHidingEnabled} />
                </LoadingContainer>
            </DocumentTitle>
        );
    }
}

export default withCoordinationClient(injectIntl(withMatchScreenSize(sizes.medium)(HelpCenter)));
